import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import Tabs from "../../atoms/Tabs/Tabs";
import Filters from "../../templates/Filters/Filters";
import Image from "../../atoms/Image/Image";
import cancellationIcon from "../../../assets/images/clock-close.png";
import Button from "../../atoms/Button/Button";
import useRequest from "../../../context/useRequest";
import { Pagination } from "react-bootstrap";

const SearchPage = () => {
  const { sendRequest } = useRequest();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState({});
  const [searchResult, setSearchResult] = useState({});
  const [originalResults, setOriginalResults] = useState({
    results: [],
    result_count: 0,
  });
  const [displayResults, setDisplayResults] = useState({
    results: [],
    result_count: 0,
  });
  

  // Pagination state
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 5; // Number of items per page

  useEffect(() => {
    if (location.state?.data) {
      setSearchData(location.state.data?.search_data);
      const searchResults = location.state.data?.search_results || {
        results: [],
        result_count: 0,
      };
      setOriginalResults(searchResults);
      setDisplayResults(searchResults);
      setActivePage(1);
    }
  }, [location.state]);

  const handleBooking = async (bookingToken) => {
    try {
      if (!bookingToken) {
        console.error("Booking token is missing.");
        return;
      }

      const response = await sendRequest({
        method: "POST",
        url: "/cab/fareQoute",
        body: { booking_token: bookingToken },
      });

      if (response?.data) {
        const { booking_token, vehicle_details, search_data, expiry } =
          response.data;

        if (booking_token) {
          navigate("/booking", {
            state: {
              bookingToken: booking_token,
              vehicleDetails: vehicle_details,
              searchData: search_data,
              expiryData: expiry,
            },
          });
        } else {
          console.error("Missing booking token in response data");
        }
      } else {
        console.error("Failed to fetch fare quote or missing data in response");
      }
    } catch (error) {
      console.error("Error during booking API call:", error);
    }
  };

  // Apply filters based on selected categories
  const handleFilterChange = (selectedCategories) => {
    if (selectedCategories.length === 0) {
      setDisplayResults(originalResults);
    } else {
      const filtered = {
        results: originalResults.results.filter((car) =>
          selectedCategories.includes(car.vehicle_category)
        ),
        result_count: originalResults.results.filter((car) =>
          selectedCategories.includes(car.vehicle_category)
        ).length,
      };
      setActivePage(1);
      setDisplayResults(filtered);
    }
  };

  // Apply sorting to currently displayed results
  const handleSortChange = (sortOrder) => {
    const sorted = {
      results: [...displayResults.results].sort((a, b) =>
        sortOrder === "low-to-high"
          ? a.base_fare - b.base_fare
          : b.base_fare - a.base_fare
      ),
      result_count: displayResults.result_count,
    };
    setActivePage(1);
    setDisplayResults(sorted);
  };

  // Calculate pagination values
  const totalPages = Math.ceil(displayResults.results.length / itemsPerPage);
  const currentItems = displayResults.results.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  // Pagination item renderer
  const renderPaginationItems = () => {
    let items = [];
    const maxVisiblePages = 5; // Maximum pages to show in pagination

    // Always show first page
    items.push(
      <Pagination.Item
        key={1}
        active={1 === activePage}
        onClick={() => setActivePage(1)}
      >
        1
      </Pagination.Item>
    );

    // Show ellipsis if current page is far from start
    if (activePage > 3) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    // Calculate range of pages to show
    let startPage = Math.max(2, activePage - 1);
    let endPage = Math.min(totalPages - 1, activePage + 1);

    // Adjust if we're near the start or end
    if (activePage <= 3) {
      endPage = Math.min(4, totalPages - 1);
    } else if (activePage >= totalPages - 2) {
      startPage = Math.max(totalPages - 3, 2);
    }

    // Add page numbers
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === activePage}
          onClick={() => setActivePage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Show ellipsis if current page is far from end
    if (activePage < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" />);
    }

    // Always show last page if there's more than one page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === activePage}
          onClick={() => setActivePage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const priceFormat = (price) => {
    if (isNaN(price)) return "0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  return (
    <>
      <div className="hero-section search-modify section-padding">
        <Container>
          <Row>
            <Col className="col-12 ">
              <Tabs modifySearch={true} searchData={searchData} />
            </Col>
          </Row>
        </Container>
      </div>
      <div className="search-product section-padding">
        <Container>
          <Row>
            <Col className="col-12 col-lg-3">
              <Filters
                searchResult={originalResults}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
              />
            </Col>
            <Col className="col-12 col-lg-9">
              {currentItems.length > 0 ? (
                <>
                  {currentItems.map((result, index) => (
                    <>
                      <div
                        key={result.id || index}
                        className="search-product--cab"
                      >
                        <div className="search-product--cab-left w-100">
                          <figure className="mb-3 col-lg-0 search-product--cab-left-image">
                            <Image
                              src={result.image}
                              alt={result.name}
                              width="450"
                              height="350"
                            />
                          </figure>
                          <div className="search-product--cab-left-info">
                            <h3 className="h5">{result.name}</h3>
                            <ul className="search-product--cab-left-info-table">
                              <li className="mb-0">{result.name}</li>
                              <li className="mb-0">{result.seats} Seats</li>
                            </ul>

                            <div className="search-product--cab-left-details">
                              <h3 className="h6 mb-2">Details</h3>
                              <div className="search-product--cab-left-details-fare">
                                <div className="search-product--cab-left-details-fare-left">
                                  <figure className="mb-0 search-product--cab-left-details-fare-left-icon">
                                    <img
                                      src={cancellationIcon}
                                      alt="subTitle"
                                    />
                                  </figure>
                                  <h6 className="h6 mb-0">Cancellation</h6>
                                </div>
                                <div className="search-product--cab-left-details-fare-right">
                                  <p className="mb-0">
                                    Upto 5 hours before the pickup
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="search-product--cab-pricing">
                        <h4>{priceFormat(result.base_fare)}</h4>
                          <Button
                            className="btn-custom"
                            onClick={async () => {
                              await handleBooking(result.booking_token);
                            }}
                            label="Book Now"
                          />
                        </div>
                      </div>
                    </>
                  ))}
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4 booking-history-pagination">
                      <Pagination>
                        <Pagination.Prev
                          onClick={() =>
                            setActivePage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={activePage === 1}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          onClick={() =>
                            setActivePage((prev) =>
                              Math.min(prev + 1, totalPages)
                            )
                          }
                          disabled={activePage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <p
                  className="text-center error"
                  style={{ border: "1px solid #d3d3d3" }}
                >
                  No search results found.
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default SearchPage;
